// markerHelpers.js

import { MapUtils } from '../uitls/mapUtils'
import { offsetCenter } from './mapHelpers'
import { startLiveLocation, stopLiveLocation } from '../utils/locationUtils.js'

// Variable globale pour stocker l'ID du watcher
let userPositionWatcher = null

/**
 * Gestion des markers et de la position utilisateur
 */
export async function refreshUserPosition(markersArray, options = {}) {
  try {
    const opts = typeof options === 'boolean' ? { preferCache: options } : options
    const { preferCache = true, onRefined = null, ...customOpts } = opts

    const onProgressiveUpdate = (refinedPos) => {
      if (!refinedPos?.lat || !refinedPos?.lng) return
      const userMarker = {
        isUserPosition: true,
        position: { lat: refinedPos.lat, lng: refinedPos.lng },
        heading: refinedPos.heading,
        speed: refinedPos.speed,
        content: 'Votre position',
      }

      const idx = markersArray.findIndex(m => m.isUserPosition)
      if (idx !== -1) markersArray[idx] = userMarker
      else markersArray.push(userMarker)

      if (onRefined && typeof onRefined === 'function') {
        onRefined(userMarker)
      }
    }

    // Utiliser la fonction avec fallback intelligent et support cache
    const pos = await MapUtils.getCurrentPositionWithFallback({
      preferCache,
      onProgressiveUpdate,
      ...customOpts,
    })
    if (!pos?.lat || !pos?.lng) return null

    const userMarker = {
      isUserPosition: true,
      position: { lat: pos.lat, lng: pos.lng },
      heading: pos.heading,
      speed: pos.speed,
      content: 'Votre position',
    }

    const idx = markersArray.findIndex(m => m.isUserPosition)
    if (idx !== -1) markersArray[idx] = userMarker
    else markersArray.push(userMarker)

    return userMarker.position
  } catch (error) {
    // Si même le fallback échoue, on ne peut rien faire
    console.warn(
      'Impossible de récupérer la position utilisateur (même avec fallback):',
      error instanceof Error ? error.message : String(error),
    )
    return null
  }
}

/**
 * Démarre le suivi en temps réel de la position utilisateur
 * @param {Array} markersArray - tableau des markers
 * @param {Function} onUpdate - callback optionnel pour les mises à jour
 */
export async function startRealTimeUserTracking(markersArray, onUpdate = null) {
  // Arrêter le suivi précédent s'il existe
  stopRealTimeUserTracking()

  userPositionWatcher = await startLiveLocation(
    positionData => {
      // Mettre à jour le marker utilisateur
      const userMarker = {
        isUserPosition: true,
        position: { lat: positionData.lat, lng: positionData.lng },
        heading: positionData.heading,
        speed: positionData.speed,
        content: 'Votre position',
      }

      const idx = markersArray.findIndex(m => m.isUserPosition)
      if (idx !== -1) markersArray[idx] = userMarker
      else markersArray.push(userMarker)

      // Appeler le callback de mise à jour si fourni
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate(userMarker)
      }
    },
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 3000,
    },
  )

  return userPositionWatcher
}

/**
 * Arrête le suivi en temps réel de la position utilisateur
 */
export function stopRealTimeUserTracking() {
  if (userPositionWatcher) {
    stopLiveLocation(userPositionWatcher)
    userPositionWatcher = null
  }
}

export async function centerOnUser(map, markersArray, offsetY, explicitPos = null) {
  try {
    if (!map) return

    // 1. Si une position explicite est fournie, centrer directement
    if (explicitPos && explicitPos.lat && explicitPos.lng) {
      map.panTo(offsetCenter(explicitPos, offsetY, map))
      return
    }

    // 2. Si le marqueur utilisateur existe déjà, centrer immédiatement
    const existingUserMarker = markersArray.find(m => m.isUserPosition)
    if (existingUserMarker && existingUserMarker.position?.lat) {
      map.panTo(offsetCenter(existingUserMarker.position, offsetY, map))
      return
    }

    // 3. Sinon, récupérer rapidement la position avec le cache
    const userPos = await refreshUserPosition(markersArray, { preferCache: true })
    if (!userPos) return
    map.panTo(offsetCenter(userPos, offsetY, map))
  } catch (error) {
    // Erreur de centrage sur l'utilisateur - gérable localement
    console.warn(
      'Impossible de centrer sur la position utilisateur:',
      error instanceof Error ? error.message : String(error),
    )
  }
}

export function addStepMarker(marker, markersArray, routeId = null) {
  markersArray.push({
    position: { lat: marker.lat, lng: marker.lng },
    isUserPosition: false,
    routeId,
    options: { title: marker.label || 'Étape' },
  })
}
